import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppData } from '../store';
import * as db from '../lib/db';
import { PROJECT_STATUS_LABELS, PROJECT_STATUS_ORDER, type ProjectStatus } from '../types';

export default function ProjectForm() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { properties, projects, refresh } = useAppData();

  const editing = projectId ? projects.find((p) => p.id === projectId) : undefined;
  const presetPropertyId = (location.state as { propertyId?: string } | null)?.propertyId ?? null;

  const [name, setName] = useState(editing?.name ?? '');
  const [propertyId, setPropertyId] = useState<string | ''>(
    editing?.property_id ?? presetPropertyId ?? ''
  );
  const [status, setStatus] = useState<ProjectStatus>(editing?.status ?? 'idea');
  const [targetTimeframe, setTargetTimeframe] = useState(editing?.target_timeframe ?? '');
  const [estimatedBudget, setEstimatedBudget] = useState(
    editing?.estimated_budget?.toString() ?? ''
  );
  const [actualSpent, setActualSpent] = useState(editing?.actual_spent?.toString() ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        property_id: propertyId || null,
        status,
        target_timeframe: targetTimeframe.trim() || null,
        estimated_budget: estimatedBudget ? Number(estimatedBudget) : null,
        actual_spent: actualSpent ? Number(actualSpent) : null,
        notes: notes.trim() || null,
      };

      if (editing) {
        await db.updateProject(editing.id, payload);
        await refresh();
        navigate(`/projects/${editing.id}`);
      } else {
        const created = await db.createProject(payload);
        await refresh();
        navigate(`/projects/${created.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="form card" onSubmit={handleSubmit}>
      <h2>{editing ? 'Edit project' : 'Add a project'}</h2>

      <label>
        Name
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Redo the deck, New build"
          required
          autoFocus
        />
      </label>

      <label>
        Property (optional)
        <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
          <option value="">No specific property</option>
          {properties.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>

      <label>
        Status
        <select value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus)}>
          {PROJECT_STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {PROJECT_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </label>

      <label>
        Target timeframe (optional)
        <input
          value={targetTimeframe}
          onChange={(e) => setTargetTimeframe(e.target.value)}
          placeholder="e.g. Spring 2027, no fixed date needed"
        />
      </label>

      <label>
        Estimated budget (optional)
        <input
          type="number"
          inputMode="decimal"
          value={estimatedBudget}
          onChange={(e) => setEstimatedBudget(e.target.value)}
          placeholder="0.00"
        />
      </label>

      <label>
        Actual spent so far (optional)
        <input
          type="number"
          inputMode="decimal"
          value={actualSpent}
          onChange={(e) => setActualSpent(e.target.value)}
          placeholder="0.00"
        />
      </label>

      <label>
        Notes
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />
      </label>

      {error && <p className="error">{error}</p>}

      <div className="form-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate(editing ? `/projects/${editing.id}` : '/projects')}
        >
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
